import React, { Dispatch, Fragment, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/buttons/Button';
import InputSkeleton from '../../../components/loaders/InputSkeleton';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import useModelSelectOptions from '../../../hooks/useModelSelectOptions';
import { customStyles } from '../../../utils/SelectStyles';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { TOptions } from '../../../types/types';
import { IVariantData } from '../../variants/types/interfaces';
import { useParams } from 'react-router-dom';
import { hasPermission } from '../../../utils/HelperFunctions';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandAccordion from '../../../components/ExpandesAccordion';
import { generalGet } from '../../../API/api';
import { useQuery } from '@tanstack/react-query';
import Tabs from '../../../components/Tabs';
import { detailsIcon, manualInputIcon } from '../../../config/variables';

export default function CreateOrderFormInputs({
  formik,
  expanded,
  setExpanded,
  currentOrderData
}: {
  formik: FormikProps<any>;
  expanded: number;
  setExpanded: Dispatch<React.SetStateAction<number>>;
  currentOrderData: any;
}) {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [firstChanged, setFirstChanged] = useState(false);
  const [chassisShow, setChassisShow] = useState(false);
  const [vehicleData, setVehicleData] = useState<any>(currentOrderData?.vehicle || 0);
  const [loading, setLoading] = useState(false);

  const viewStock = () => {
    setLoading(true);
    generalGet(`/admin/vehicles/chassis/${formik.values.chassis}`)
      .then((data) => {
        setLoading(false);
        const vehicleData = data?.data?.data;
        if (vehicleData) {
          formik.setFieldValue(`vehicle_id`, vehicleData.id);
          formik.setFieldError('chassis', undefined);
          formik.setFieldValue(`vehicle_price`, vehicleData.variant?.price);
          console.log(vehicleData.variant, 'vehicleData.variant');
          formik.setFieldValue(`model_id`, vehicleData.variant?.vehicle_model?.id);
        }
        setVehicleData(data?.data?.data || null);
      })
      .catch((error) => {
        setLoading(false);
        formik?.setFieldError('chassis', error?.response?.data?.message || t('somethingWrong'));
        // toast.error(error?.response?.data?.message || t('somethingWrong'));
      });
  };

  useEffect(() => {
    setVehicleData(currentOrderData?.vehicle || 0);
    formik.setFieldValue(`chassis`, currentOrderData?.vehicle?.chassis);
    formik.setFieldValue(`vehicle_id`, currentOrderData?.vehicle?.id);
    formik.setFieldValue(`model_id`, currentOrderData?.vehicle?.variant?.vehicle_model?.id);
  }, [currentOrderData?.vehicle]);

  const { modelOptions: makesOptions } = useModelSelectOptions({
    labelKey: ['name'],
    valueKey: 'id',
    modelRoute: 'admin/makes',
    queryKey: ['makes']
  });

  const { modelOptions, apiDataLoading: modelApiDataLoading } = useModelSelectOptions({
    labelKey: [`name[${i18n.language}]`],
    valueKey: 'id',
    modelRoute: `admin/makes/${formik?.values?.make_id}/models`,
    queryKey: ['models', formik?.values?.make_id],
    enabled: !!formik.values?.make_id
  });

  const { data: warranties, isLoading: warrantiesApiDataLoading } = useQuery({
    queryKey: ['warranties', formik?.values?.model_id],
    queryFn: () => generalGet(`/admin/models/${formik?.values?.model_id}/warranties`),
    enabled: !!formik?.values?.model_id
  });

  const warrantiesOptions = warranties?.data?.data?.map((item: any) => ({
    label: (
      <div className="f a-c s-b">
        <span>{item?.title?.[i18n.language]}</span>
        <span>{item?.price ? `${Number(item?.price)?.toLocaleString('en-US')} ${t('sar')}` : t('included')}</span>
      </div>
    ),
    value: item?.id
  }));

  const {
    modelOptions: variantOptions,
    apiDataLoading: variantApiDataLoading,
    apiData: variantsFullData
  } = useModelSelectOptions({
    labelKey: [`display_name_translation[${i18n.language}]`],
    valueKey: 'id',
    modelRoute: `admin/models/${formik?.values?.model_id}/variants?with=all_exterior_colors,all_interior_colors`,
    queryKey: ['variants', formik?.values?.model_id],
    enabled: !!formik.values?.model_id
  });

  const { modelOptions: chassisOptions, apiDataLoading: chassisApiDataLoading } = useModelSelectOptions({
    labelKey: 'chassis',
    valueKey: 'chassis',
    modelRoute: `admin/vehicles/filter?variant_id=${formik?.values?.variant_id}&exterior_color_id=${formik?.values?.exterior_color_id}&interior_color_id=${formik?.values?.interior_color_id}&in_stock=1`,
    queryKey: ['vehicles', formik?.values?.variant_id, formik?.values?.exterior_color_id, formik?.values?.interior_color_id],
    enabled: !!formik.values?.variant_id && !!formik?.values?.exterior_color_id && !!formik?.values?.interior_color_id
  });

  const { exteriorColorsOptions, interiorColorsOptions } = useMemo(() => {
    if (!formik?.values?.variant_id)
      return {
        exteriorColorsOptions: [],
        interiorColorsOptions: []
      };

    const selectedVariant = variantsFullData?.find((item: IVariantData) => item.id === formik?.values?.variant_id);
    const exteriorColorsOptions = selectedVariant?.all_exterior_colors?.map((color: any) => ({ label: color?.display_name?.[i18n.language], value: color?.id })) || [];
    const interiorColorsOptions = selectedVariant?.all_interior_colors?.map((color: any) => ({ label: color?.display_name?.[i18n.language], value: color?.id })) || [];

    if (formik?.values?.variant_id == currentOrderData?.vehicle?.variant?.id) {
      formik?.setFieldValue('vehicle_price', currentOrderData?.vehicle_price || selectedVariant?.price);
    } else {
      formik?.setFieldValue('vehicle_price', selectedVariant?.price);
    }

    return { exteriorColorsOptions, interiorColorsOptions };
  }, [formik?.values?.variant_id, variantsFullData]);

  useEffect(() => {
    if (!firstChanged && id) {
      if (formik?.values?.make_id) setFirstChanged(true);
      return;
    }
    formik.setFieldValue('model_id', '');
    formik.setFieldValue('variant_id', '');
    formik.setFieldValue('exterior_color_id', '');
    formik.setFieldValue('interior_color_id', '');
    formik.setFieldValue('chassis', '');
  }, [formik.values?.make_id]);

  useEffect(() => {
    if (formik?.values?.manual_chassis) return;
    if (!firstChanged && id) {
      if (formik?.values?.model_id) setFirstChanged(true);
      return;
    }
    formik.setFieldValue('variant_id', '');
    formik.setFieldValue('exterior_color_id', '');
    formik.setFieldValue('interior_color_id', '');
    formik.setFieldValue('chassis', '');
  }, [formik.values?.model_id]);

  useEffect(() => {
    if (formik?.values?.manual_chassis) return;
    if (!firstChanged && id) {
      if (formik?.values?.variant_id) setFirstChanged(true);
      return;
    }
    formik.setFieldValue('exterior_color_id', '');
    formik.setFieldValue('interior_color_id', '');
    formik.setFieldValue('chassis', '');
  }, [formik.values?.variant_id]);

  useEffect(() => {
    if (makesOptions?.length == 1 && formik?.values?.make_id != makesOptions?.[0]?.value) {
      formik?.setFieldValue('make_id', makesOptions?.[0]?.value);
    }
  }, [makesOptions]);

  useEffect(() => {
    if (formik?.values?.chassis != currentOrderData?.vehicle?.chassis) {
      formik?.setFieldValue('vehicle_id', '');
    }
  }, [formik?.values?.chassis]);

  useEffect(() => {
    if (formik?.values?.vehicle_id) {
      formik?.setFieldError('chassis', undefined);
    }
  }, [formik?.values?.vehicle_id]);

  const tabs = useMemo(
    () => [
      {title: (
        <Fragment>
          <span className='f'>{manualInputIcon}</span> <span>{t('manual_chassis')}</span>
        </Fragment>
      ) , value: 'manual_chassis' },
      { title: (
        <Fragment>
          <span className='f'>{detailsIcon}</span> <span>{t('choose_car')}</span>
        </Fragment>
      ) , value: 'choose_car' }
    ],
    [t]
  );

  const canEditVehicle = !id || (id && chassisShow);

  return (
    <div className="field-array-container">
      <Accordion
        expanded={expanded == 2}
        onChange={(e) => {
          e.stopPropagation();
          setExpanded(expanded == 2 ? -1 : 2);
        }}
      >
        <AccordionSummary
          sx={{
            '&.Mui-focusVisible': {
              backgroundColor: '#000'
            }
          }}
          aria-controls="panel1a-content"
          id="panel1a-header"
          className={`header-container ${expanded != 2 && 'expand'}`}
        >
          <p>
            <ExpandAccordion expand={expanded == 2} /> {t('car_details')}
          </p>
        </AccordionSummary>
        <AccordionDetails>
          <div className="inputs_grid">
            <Tabs
              tabs={tabs}
              value={formik.values.manual_chassis == 1 ? 'manual_chassis' : 'choose_car'}
              setValue={(value: string) => {
                formik.setFieldValue('manual_chassis', value == 'manual_chassis' ? 1 : 0);
              }}
              theme="secondary"
              customClass="grid-12"
            />
            {/* {canEditVehicle && (
              <div className="switch_container grid-12">
                <SwitchActivation
                  onChange={(value: any) => {
                    formik.setFieldValue('manual_chassis', value?.target?.checked ? 1 : 0);
                  }}
                  active={formik.values.manual_chassis == 1}
                />
                <p>{t('manual_chassis')}</p>
              </div>
            )} */}
            {id && !chassisShow && (
              <Fragment>
                <FieldWrapper customPadding title={t('current_chassis')} gridSize={6}>
                  <div className="current_chassis_cont">
                    <div>{formik?.values?.vehicle?.chassis}</div>
                    <Button
                      onClick={() => {
                        setChassisShow(true);
                      }}
                    >
                      <span className="bold">{t('change')}</span>
                    </Button>
                  </div>
                </FieldWrapper>
                <FieldWrapper gridSize={6} />
              </Fragment>
            )}
            {formik.values.manual_chassis == 1 ? (
              // Manual chassis mode - show chassis input with load stock button
              <Fragment>
                {canEditVehicle && (
                  <FieldWrapper title={t('chassisNo')} noPadding>
                    <div className="inputs_group special-mobile">
                      <FieldWrapper inputName={`chassis`} inputPlaceholder={t('searchByChassisNo')} input />
                      <FieldWrapper>
                        <Button
                          loading={loading}
                          disabled={loading || !formik.values.chassis}
                          onClick={!loading && formik.values.chassis && viewStock}
                          text={t('loadStock')}
                          type={'button'}
                        ></Button>
                      </FieldWrapper>
                    </div>
                  </FieldWrapper>
                )}
                {vehicleData?.variant?.display_name && (
                  <>
                    {makesOptions?.length > 1 && (
                      <FieldWrapper
                        customPadding
                        title={t('carMake')}
                        inputPlaceholder={vehicleData?.variant?.vehicle_model?.vehicle_make.name}
                        inputName={`t`}
                        input
                        disabled
                        gridSize={6}
                      />
                    )}
                    <FieldWrapper customPadding title={t('carModel')} inputPlaceholder={vehicleData?.variant?.vehicle_model?.name} inputName={`t`} input disabled gridSize={6} />
                    <FieldWrapper
                      customPadding
                      title={t('car_variant')}
                      inputPlaceholder={vehicleData?.variant?.display_name?.[i18n.language]}
                      inputName={`t`}
                      input
                      disabled
                      gridSize={6}
                    />
                    <FieldWrapper
                      customPadding
                      title={t('interior_color')}
                      inputPlaceholder={vehicleData?.interior_color?.display_name?.[i18n.language]}
                      inputName={`t`}
                      input
                      disabled
                      gridSize={6}
                    />
                    <FieldWrapper
                      customPadding
                      title={t('exterior_color')}
                      inputPlaceholder={vehicleData?.exterior_color?.display_name?.[i18n.language]}
                      inputName={`t`}
                      input
                      disabled
                      gridSize={6}
                    />
                  </>
                )}
              </Fragment>
            ) : canEditVehicle ? (
              // Normal mode - show vehicle selection flow
              <>
                {makesOptions?.length <= 1 ? null : (
                  <FieldWrapper
                    customPadding
                    title={t('vehicle_make')}
                    inputPlaceholder={hasPermission(['makes.show']) ? t('vehicle_make') : t('no_permission_to_show')}
                    disabled={!hasPermission(['makes.show'])}
                    inputName={`make_id`}
                    options={makesOptions}
                    selectStyle={customStyles}
                    value={makesOptions?.filter((item: TOptions) => formik.values?.make_id === item?.value)}
                    gridSize={6}
                    select
                    search
                  />
                )}
                {modelApiDataLoading ? (
                  <InputSkeleton gridSize={6} />
                ) : (
                  <FieldWrapper
                    customPadding
                    title={t('vehicle_model')}
                    inputPlaceholder={!formik.values?.make_id ? t('choose_vehicle_make_first') : hasPermission(['models.show']) ? t('vehicle_model') : t('no_permission_to_show')}
                    disabled={!formik.values?.make_id || !hasPermission(['models.show'])}
                    inputName={`model_id`}
                    options={modelOptions}
                    selectStyle={customStyles}
                    value={modelOptions?.filter((item: TOptions) => formik.values?.model_id === item?.value)}
                    gridSize={6}
                    select
                    search
                  />
                )}
                {variantApiDataLoading ? (
                  <InputSkeleton gridSize={6} />
                ) : (
                  <FieldWrapper
                    customPadding
                    title={t('variant')}
                    inputPlaceholder={!formik.values?.model_id ? t('choose_vehicle_model_first') : hasPermission(['variants.show']) ? t('variant') : t('no_permission_to_show')}
                    disabled={!formik.values?.model_id || !hasPermission(['variants.show'])}
                    inputName={`variant_id`}
                    options={variantOptions}
                    selectStyle={customStyles}
                    value={variantOptions?.filter((item: TOptions) => formik.values?.variant_id === item?.value)}
                    gridSize={6}
                    select
                    search
                  />
                )}
                {variantApiDataLoading ? (
                  <InputSkeleton gridSize={6} />
                ) : (
                  <FieldWrapper
                    customPadding
                    title={t('exterior_color')}
                    inputPlaceholder={!formik.values?.variant_id ? t('choose_vehicle_variant_first') : t('exterior_color')}
                    inputName={`exterior_color_id`}
                    options={exteriorColorsOptions}
                    selectStyle={customStyles}
                    value={exteriorColorsOptions?.filter((item: TOptions) => formik.values?.exterior_color_id === item?.value)}
                    gridSize={6}
                    select
                    search
                    disabled={!formik.values?.variant_id}
                  />
                )}
                {variantApiDataLoading ? (
                  <InputSkeleton gridSize={6} />
                ) : (
                  <FieldWrapper
                    customPadding
                    title={t('interior_color')}
                    inputPlaceholder={!formik.values?.variant_id ? t('choose_vehicle_variant_first') : t('interior_color')}
                    inputName={`interior_color_id`}
                    options={interiorColorsOptions}
                    selectStyle={customStyles}
                    value={interiorColorsOptions?.filter((item: TOptions) => formik.values?.interior_color_id === item?.value)}
                    gridSize={6}
                    select
                    search
                    disabled={!formik.values?.variant_id}
                  />
                )}
                {id && !chassisShow ? null : chassisApiDataLoading ? (
                  <InputSkeleton gridSize={6} />
                ) : (
                  <FieldWrapper
                    customPadding
                    title={t('chassis')}
                    inputPlaceholder={
                      !formik.values?.variant_id || !formik.values?.exterior_color_id || !formik.values?.interior_color_id
                        ? t('choose_vehicle_variant_exterior_interior_first')
                        : hasPermission(['vehicles.show'])
                          ? t('chassis')
                          : t('no_permission_to_show')
                    }
                    disabled={!formik.values?.variant_id || !formik.values?.exterior_color_id || !formik.values?.interior_color_id || !hasPermission(['vehicles.show'])}
                    inputName={`chassis`}
                    options={chassisOptions}
                    selectStyle={customStyles}
                    value={chassisOptions?.filter((item: TOptions) => formik.values?.chassis === item?.value)}
                    gridSize={6}
                    select
                    search
                  />
                )}
              </>
            ) : null}
            {variantApiDataLoading ? (
              <InputSkeleton gridSize={6} />
            ) : (
              <FieldWrapper title={t('vehicle_price')} inputName={`vehicle_price`} inputPlaceholder={t('vehicle_price')} input customPadding gridSize={6} />
            )}
            {!id && <FieldWrapper title={t('paid_amount')} inputName={`paid_amount`} inputPlaceholder={t('paid_amount')} input customPadding gridSize={6} />}
            {warrantiesApiDataLoading ? (
              <InputSkeleton gridSize={6} />
            ) : (
              <FieldWrapper
                customPadding
                title={t('warranty')}
                inputPlaceholder={!formik.values?.model_id ? t('choose_vehicle_model_first') : t('warranty')}
                disabled={!formik.values?.model_id}
                inputName={`warranty_id`}
                options={warrantiesOptions}
                selectStyle={customStyles}
                value={warrantiesOptions?.filter((item: TOptions) => formik.values?.warranty_id === item?.value)}
                gridSize={6}
                select
                search
              />
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
